import React from "react";
import { StyleSheet, View } from "react-native";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { LiftColors, theme } from "../theme";
import { NavigationContainer, NavigationIndependentTree } from "@react-navigation/native";
import SnackBarPortal from "../components/SnackbarZone";
import { EventProvider } from "react-native-outside-press";
import DropdownManager from "../contexts/DropdownManager";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { useLiftFont } from "../contexts/AppLoadingManager";
import { ImageCropZoomPortal } from "../components/cropZoom/ImageCropZoomModal";
import WebImageChooserPortal from "../components/webimages/WebimageChooserPortal";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { ThemeProvider } from "../contexts/ThemeManager";
import { IntercomProvider } from "../contexts/IntercomProvider";
import TransparentOverlayPortal from "../components/TransparentOverlayPortal";
import ModalPortal from "../components/ModalPortal";
import KeyboardStickyComponentManager from "../contexts/KeyboardStickyComponentManager";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";

const style = StyleSheet.create({
    main: {
        flex: 1,
        justifyContent: "center",
        alignItems: "stretch",
        width: "100%",
    },
});

type Back = "surface" | "variant" | "background" | "container" | "gray";
type Position = "top" | "center" | "bottom";

type CenterViewProps = {
    children: React.ReactNode;
    back?: Back;
    position?: Position;
};

const getBackground = (colors: LiftColors, back?: Back) => {
    switch (back) {
        case "surface":
            return colors.surface;
        case "variant":
            return colors.surfaceVariant;
        case "background":
            return colors.background;
        case "container":
            return colors.primaryContainer;
        case "gray":
            return "#DDD";
        default:
            return colors.surface;
    }
};

const getJustifyContent = (position?: Position) => {
    switch (position) {
        case "top":
            return "flex-start";
        case "center":
            return "center";
        case "bottom":
            return "flex-end";
        default:
            return "center";
    }
};
const StorybookContainer = ({ children, back, position }: CenterViewProps) => {
    const [loaded] = useLiftFont();
    if (!loaded) {
        return null;
    }
    return (
        <IntercomProvider>
            <ThemeProvider value={theme}>
                <View
                    style={{
                        flex: 1,
                        backgroundColor: getBackground(theme.colors, back),
                    }}
                >
                    <ActionSheetProvider>
                        <EventProvider>
                            <SafeAreaProvider>
                                <DropdownManager>
                                    <GestureHandlerRootView style={{ flex: 1 }}>
                                        <KeyboardProvider>
                                            <View
                                                style={[
                                                    style.main,
                                                    {
                                                        backgroundColor: getBackground(
                                                            theme.colors,
                                                            back,
                                                        ),
                                                        justifyContent: getJustifyContent(position),
                                                    },
                                                ]}
                                            >
                                                <NavigationIndependentTree>
                                                    <NavigationContainer
                                                        theme={theme}
                                                        navigationInChildEnabled
                                                    >
                                                        <BottomSheetModalProvider>
                                                            <KeyboardStickyComponentManager>
                                                                {children}
                                                                <ModalPortal />
                                                            </KeyboardStickyComponentManager>
                                                        </BottomSheetModalProvider>
                                                    </NavigationContainer>
                                                </NavigationIndependentTree>
                                                <SnackBarPortal />
                                                <TransparentOverlayPortal />
                                                <ImageCropZoomPortal />
                                                <WebImageChooserPortal />
                                            </View>
                                        </KeyboardProvider>
                                    </GestureHandlerRootView>
                                </DropdownManager>
                            </SafeAreaProvider>
                        </EventProvider>
                    </ActionSheetProvider>
                </View>
            </ThemeProvider>
        </IntercomProvider>
    );
};

export default StorybookContainer;
